import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Gift, 
  Copy, 
  Share2, 
  DollarSign, 
  Users, 
  Check,
  ArrowRight,
  Sparkles,
  Wallet,
  History,
  Download,
  Lock,
  QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Referral() {
  const navigate = useNavigate();
  const { isAuthenticated, user, referralCode, credits, checkAuth, loading: authLoading, referredBy, isSubscriber } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState({ 
    subscription_price: 9.99, 
    referrer_credit_percent: 10, 
    referee_credit_percent: 10 
  });
  const [applyCode, setApplyCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloadingPoster, setDownloadingPoster] = useState(false);

  // Fetch pricing from public endpoint (no auth required)
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pricing/public`);
        if (response.ok) {
          const data = await response.json();
          setPricing({
            subscription_price: data.subscription_price || 9.99,
            trial_days: data.trial_days || 7,
            referrer_credit_percent: data.referrer_credit_percent || 10,
            referee_credit_percent: data.referee_credit_percent || 10
          });
        }
      } catch (err) {
        console.error('Failed to fetch pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  // Calculate referral credits dynamically
  const referrerCredit = (pricing.subscription_price * (pricing.referrer_credit_percent || 10)) / 100;
  const refereeCredit = (pricing.subscription_price * (pricing.referee_credit_percent || 10)) / 100;

  useEffect(() => {
    // Wait for auth to finish loading before checking
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchReferralStats();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchReferralStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referral/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch referral stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/signup?ref=${referralCode}`;
    const shareText = `Join SportsAnalysisAB and get $${refereeCredit.toFixed(2)} credit! Use my code: ${referralCode}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'SportsAnalysisAB Referral',
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or share failed
        handleCopyCode();
      }
    } else {
      handleCopyCode();
    }
  };

  const handleDownloadPoster = async () => {
    setDownloadingPoster(true);
    try {
      const response = await fetch(`${API_URL}/api/referral/poster`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sportsanalysisab_referral_${stats?.referral_code || 'poster'}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Poster downloaded!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to download poster');
      }
    } catch (err) {
      toast.error('Failed to download poster');
    } finally {
      setDownloadingPoster(false);
    }
  };

  const handleApplyCode = async (e) => {
    e.preventDefault();
    if (!applyCode.trim()) return;
    
    setApplying(true);
    try {
      const response = await fetch(`${API_URL}/api/referral/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ referral_code: applyCode.trim() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setApplyCode('');
        await checkAuth(); // Refresh user data
        await fetchReferralStats();
      } else {
        toast.error(data.detail || 'Failed to apply code');
      }
    } catch (err) {
      toast.error('Failed to apply referral code');
    } finally {
      setApplying(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-8 h-8 text-primary" />
              <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                Referral Program
              </h1>
            </div>
            <p className="text-muted-foreground text-lg">
              Share with friends and both get <span className="text-primary font-semibold">{pricing.referrer_credit_percent}% credit</span> toward your subscription
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Subscriber Only Notice */}
          {!isSubscriber && (
            <Card className="mb-8 border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <Lock className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-yellow-500">Subscribe to Unlock Referrals</p>
                      <p className="text-sm text-muted-foreground">
                        Only premium subscribers can share referral codes and earn credits
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/billing')}
                    className="btn-blaze rounded-sm"
                  >
                    Subscribe Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How it works */}
          <Card className="mb-8 bg-gradient-to-r from-primary/10 to-transparent border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Sparkles className="w-5 h-5 text-primary" />
                How It Works
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-semibold">Share Your Code</p>
                    <p className="text-sm text-muted-foreground">Give your unique referral code to friends</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-semibold">They Sign Up</p>
                    <p className="text-sm text-muted-foreground">Friend applies your code after registering</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-semibold">Both Get ${referrerCredit.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">{pricing.referrer_credit_percent}% credit for you and your friend!</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Your Referral Code */}
            <Card className={!isSubscriber ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Your Referral Code
                  {!isSubscriber && <Lock className="w-4 h-4 text-muted-foreground" />}
                </CardTitle>
                <CardDescription>Share this code with friends to earn credits</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 bg-secondary rounded-sm p-4 font-mono text-2xl font-bold tracking-wider text-center">
                    {isSubscriber ? (stats?.referral_code || referralCode || 'Loading...') : '********'}
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="h-14 w-14"
                    onClick={handleCopyCode}
                    disabled={!isSubscriber}
                    data-testid="copy-referral-code"
                  >
                    {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button 
                    className="flex-1" 
                    onClick={handleShare}
                    disabled={!isSubscriber}
                    data-testid="share-referral"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Button 
                    variant="outline"
                    className="flex-1" 
                    onClick={handleDownloadPoster}
                    disabled={!isSubscriber || downloadingPoster}
                    data-testid="download-poster"
                  >
                    {downloadingPoster ? (
                      <span className="animate-spin">...</span>
                    ) : (
                      <>
                        <QrCode className="w-4 h-4 mr-2" />
                        QR Poster
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Your Credits */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Your Credits
                </CardTitle>
                <CardDescription>Use credits toward your monthly subscription</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <p className="text-5xl font-bold font-mono text-primary">
                    ${(stats?.credits || credits || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Available balance
                  </p>
                </div>
                
                {(stats?.credits || credits || 0) >= pricing.subscription_price ? (
                  <Badge className="w-full justify-center py-2 bg-green-500/20 text-green-400 border-green-500/30">
                    <Check className="w-4 h-4 mr-1" />
                    Covers full subscription!
                  </Badge>
                ) : (stats?.credits || credits || 0) > 0 ? (
                  <div className="space-y-2">
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ width: `${Math.min(100, ((stats?.credits || credits || 0) / pricing.subscription_price) * 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      ${(pricing.subscription_price - (stats?.credits || credits || 0)).toFixed(2)} more to cover subscription
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center">
                    Invite friends to start earning credits
                  </p>
                )}

                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => navigate('/billing')}
                  data-testid="go-to-billing"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Go to Billing
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Apply a Code */}
          {!referredBy && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="w-5 h-5" />
                  Have a Referral Code?
                </CardTitle>
                <CardDescription>Apply a friend's code to get ${refereeCredit.toFixed(2)} credit</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleApplyCode} className="flex gap-2">
                  <Input
                    placeholder="Enter referral code"
                    value={applyCode}
                    onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                    className="font-mono uppercase"
                    maxLength={8}
                    data-testid="apply-code-input"
                  />
                  <Button type="submit" disabled={applying || !applyCode.trim()} data-testid="apply-code-btn">
                    {applying ? 'Applying...' : 'Apply Code'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-sm bg-primary/10">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">{stats?.referral_count || 0}</p>
                    <p className="text-sm text-muted-foreground">Friends Referred</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-sm bg-green-500/10">
                    <DollarSign className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">${(stats?.total_earned || 0).toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Total Earned</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-sm bg-yellow-500/10">
                    <Sparkles className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold font-mono">${referrerCredit.toFixed(2)}</p>
                    <p className="text-sm text-muted-foreground">Per Referral</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Transaction History */}
          {stats?.transactions && stats.transactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Credit History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats.transactions.map((tx) => (
                    <div 
                      key={tx.transaction_id} 
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm"
                    >
                      <div>
                        <p className="font-medium text-sm">{tx.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(tx.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Badge 
                        variant={tx.amount > 0 ? "default" : "secondary"}
                        className={tx.amount > 0 ? "bg-green-500/20 text-green-400" : ""}
                      >
                        {tx.amount > 0 ? '+' : ''}{tx.amount.toFixed(2)} CAD
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
